import React, { useState, useEffect, useCallback } from 'react';
import { Copy, Download, Upload, RotateCcw, Maximize2, Minimize2, BarChart3, Search, Zap, CheckCircle2 } from 'lucide-react';

interface ProcessingStats {
  charactersProcessed: number;
  entitiesFound: number;
  processingTime: number;
  operationsCount: number;
}

interface ProcessingResult {
  result: string;
  stats: Omit<ProcessingStats, 'operationsCount'>;
}

const RegexEscapeUnescapeTool: React.FC = () => {
  const [inputText, setInputText] = useState<string>('');
  const [outputText, setOutputText] = useState<string>('');
  const [mode, setMode] = useState<'escape' | 'unescape'>('escape');
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [stats, setStats] = useState<ProcessingStats>({
    charactersProcessed: 0,
    entitiesFound: 0,
    processingTime: 0,
    operationsCount: 0
  });
  const [notification, setNotification] = useState<string>('');

  const escapeRegex = useCallback((text: string): ProcessingResult => {
    const startTime = performance.now();
    let result = text;
    let entitiesCount = 0;

    // Regex special characters that need escaping
    const specialChars = [
      { char: '\\', escaped: '\\\\' },
      { char: '^', escaped: '\\^' },
      { char: '$', escaped: '\\$' },
      { char: '.', escaped: '\\.' },
      { char: '|', escaped: '\\|' },
      { char: '?', escaped: '\\?' },
      { char: '*', escaped: '\\*' },
      { char: '+', escaped: '\\+' },
      { char: '(', escaped: '\\(' },
      { char: ')', escaped: '\\)' },
      { char: '[', escaped: '\\[' },
      { char: ']', escaped: '\\]' },
      { char: '{', escaped: '\\{' },
      { char: '}', escaped: '\\}' }
    ];

    // Escape backslashes first to avoid conflicts
    const backslashMatches = result.match(/\\/g);
    if (backslashMatches) {
      entitiesCount += backslashMatches.length;
      result = result.replace(/\\/g, '\\\\');
    }

    // Escape other special characters
    specialChars.slice(1).forEach(({ char, escaped }) => {
      const regex = new RegExp('\\' + char, 'g');
      const matches = result.match(regex);
      if (matches) {
        entitiesCount += matches.length;
        result = result.replace(regex, escaped);
      }
    });

    const endTime = performance.now();
    return {
      result,
      stats: {
        charactersProcessed: text.length,
        entitiesFound: entitiesCount,
        processingTime: endTime - startTime
      }
    };
  }, []);

  const unescapeRegex = useCallback((text: string): ProcessingResult => {
    const startTime = performance.now();
    let result = text;
    let entitiesCount = 0;

    // Unicode escape sequences (\u{...} and \uXXXX)
    result = result.replace(/\\u\{([0-9a-fA-F]+)\}/g, (match, hex) => {
      try {
        const code = parseInt(hex, 16);
        if (code >= 0 && code <= 0x10FFFF) {
          entitiesCount++;
          return String.fromCodePoint(code);
        }
      } catch (e) {
        // Invalid unicode, return as-is
      }
      return match;
    });

    result = result.replace(/\\u([0-9a-fA-F]{4})/g, (match, hex) => {
      const code = parseInt(hex, 16);
      if (code >= 0 && code <= 0xFFFF) {
        entitiesCount++;
        return String.fromCharCode(code);
      }
      return match;
    });

    // Hexadecimal escape sequences (\xXX)
    result = result.replace(/\\x([0-9a-fA-F]{2})/g, (match, hex) => {
      const code = parseInt(hex, 16);
      if (code >= 0 && code <= 255) {
        entitiesCount++;
        return String.fromCharCode(code);
      }
      return match;
    });

    // Octal escape sequences (\XXX)
    result = result.replace(/\\([0-7]{1,3})/g, (match, oct) => {
      const code = parseInt(oct, 8);
      if (code >= 0 && code <= 255 && oct !== '0') {
        entitiesCount++;
        return String.fromCharCode(code);
      }
      return match;
    });

    // Common escape sequences
    const commonEscapes = [
      { escaped: '\\n', char: '\n' },
      { escaped: '\\r', char: '\r' },
      { escaped: '\\t', char: '\t' },
      { escaped: '\\v', char: '\v' },
      { escaped: '\\f', char: '\f' },
      { escaped: '\\b', char: '\b' },
      { escaped: '\\0', char: '\0' },
      { escaped: '\\s', char: ' ' },  // whitespace shorthand
      { escaped: '\\d', char: '[0-9]' }, // digit shorthand (conceptual)
      { escaped: '\\w', char: '[a-zA-Z0-9_]' } // word shorthand (conceptual)
    ];

    commonEscapes.forEach(({ escaped, char }) => {
      if (escaped === '\\d' || escaped === '\\w' || escaped === '\\s') {
        // These are regex shorthands, don't unescape to actual characters
        return;
      }
      
      const regex = new RegExp(escaped.replace(/\\/g, '\\\\'), 'g');
      const matches = result.match(regex);
      if (matches) {
        entitiesCount += matches.length;
        result = result.replace(regex, char);
      }
    });

    // Unescape regex special characters
    const specialChars = [
      { escaped: '\\^', char: '^' },
      { escaped: '\\$', char: '$' },
      { escaped: '\\.', char: '.' },
      { escaped: '\\|', char: '|' },
      { escaped: '\\?', char: '?' },
      { escaped: '\\*', char: '*' },
      { escaped: '\\+', char: '+' },
      { escaped: '\\(', char: '(' },
      { escaped: '\\)', char: ')' },
      { escaped: '\\[', char: '[' },
      { escaped: '\\]', char: ']' },
      { escaped: '\\{', char: '{' },
      { escaped: '\\}', char: '}' }
    ];

    specialChars.forEach(({ escaped, char }) => {
      const regex = new RegExp(escaped.replace(/\\/g, '\\\\'), 'g');
      const matches = result.match(regex);
      if (matches) {
        entitiesCount += matches.length;
        result = result.replace(regex, char);
      }
    });

    // Unescape backslashes (must be last)
    const backslashMatches = result.match(/\\\\/g);
    if (backslashMatches) {
      entitiesCount += backslashMatches.length;
      result = result.replace(/\\\\/g, '\\');
    }

    const endTime = performance.now();
    return {
      result,
      stats: {
        charactersProcessed: text.length,
        entitiesFound: entitiesCount,
        processingTime: endTime - startTime
      }
    };
  }, []);

  const processText = useCallback((): void => {
    if (!inputText.trim()) {
      setOutputText('');
      return;
    }

    const processor = mode === 'escape' ? escapeRegex : unescapeRegex;
    const { result, stats: processingStats } = processor(inputText);
    
    setOutputText(result);
    setStats(prev => ({
      ...processingStats,
      operationsCount: prev.operationsCount + 1
    }));
  }, [inputText, mode, escapeRegex, unescapeRegex]);

  useEffect(() => {
    processText();
  }, [processText]);

  const showNotification = (message: string): void => {
    setNotification(message);
    setTimeout(() => setNotification(''), 3000);
  };

  const copyToClipboard = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(outputText);
      showNotification('Copied to clipboard!');
    } catch (err) {
      showNotification('Failed to copy');
    }
  };

  const downloadFile = (): void => {
    const blob = new Blob([outputText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${mode}d-regex.txt`;
    a.click();
    URL.revokeObjectURL(url);
    showNotification('File downloaded!');
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result;
        if (typeof result === 'string') {
          setInputText(result);
          showNotification('File loaded successfully!');
        }
      };
      reader.readAsText(file);
    }
  };

  const clearAll = (): void => {
    setInputText('');
    setOutputText('');
    setStats({
      charactersProcessed: 0,
      entitiesFound: 0,
      processingTime: 0,
      operationsCount: 0
    });
    showNotification('Cleared all content!');
  };

  const toggleFullscreen = (): void => {
    setIsFullscreen(!isFullscreen);
  };

  const loadSampleRegex = (): void => {
    setInputText(`// Email validation pattern
^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$

// Phone number patterns
^\+?1?[-.\s]?\(?[0-9]{3}\)?[-.\s]?[0-9]{3}[-.\s]?[0-9]{4}$
^(\d{3})[-.\s]?(\d{3})[-.\s]?(\d{4})$

// URL validation
^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$

// Special characters to escape
Text with dots...
Question marks?
Asterisks * and plus signs +
Parentheses () and brackets []
Curly braces {} and pipe |
Caret ^ and dollar $
Backslashes \ need special handling`);
  };

  const loadSampleEscaped = (): void => {
    setInputText(`// Escaped email validation pattern
\^\\[a-zA-Z0-9\\._%-\\+\\]\\+@\\[a-zA-Z0-9\\.\\-\\]\\+\\.\\[a-zA-Z\\]\\{2,\\}\\$

// Escaped phone number patterns
\^\\\\\\+\\?1\\?\\[\\-\\.\\\\s\\]\\?\\\\\\(\\?\\[0-9\\]\\{3\\}\\\\\\)\\?\\[\\-\\.\\\\s\\]\\?\\[0-9\\]\\{3\\}\\[\\-\\.\\\\s\\]\\?\\[0-9\\]\\{4\\}\\$

// Escaped URL validation
\^\\(https\\?:\\\\/\\\\/\\)\\?\\(\\[\\\\da-z\\.\\-\\]\\+\\)\\.\\(\\[a-z\\.\\]\\{2,6\\}\\)\\(\\[\\\\/\\\\w \\.\\-\\]\\*\\)\\*\\\\/\\?\\$

// Already escaped special characters
Text with dots\\.\\.\\.
Question marks\\?
Asterisks \\* and plus signs \\+
Parentheses \\(\\) and brackets \\[\\]
Curly braces \\{\\} and pipe \\|
Caret \\^ and dollar \\$
Backslashes \\\\ need special handling`);
  };

  const testRegex = (): void => {
    if (!outputText.trim()) {
      showNotification('No regex pattern to test!');
      return;
    }
    
    try {
      new RegExp(outputText);
      showNotification('✅ Valid regex pattern!');
    } catch (error) {
      showNotification('❌ Invalid regex pattern');
    }
  };

  const containerClass = isFullscreen 
    ? "fixed inset-0 z-50 bg-gray-900" 
    : "min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900";

  return (
    <div className={containerClass}>
      <div className="container mx-auto p-4 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg">
              <Search className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Regex Escape/Unescape Tool</h1>
              <p className="text-gray-300 text-sm">Advanced regular expression processor with pattern validation</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              <BarChart3 className="w-4 h-4" />
              <span>Analytics</span>
            </button>
            
            <button
              onClick={toggleFullscreen}
              className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mode Toggle */}
        <div className="flex items-center justify-center mb-6">
          <div className="bg-gray-800 p-1 rounded-lg flex">
            <button
              onClick={() => setMode('escape')}
              className={`px-6 py-2 rounded-md transition-all flex items-center space-x-2 ${
                mode === 'escape' 
                  ? 'bg-blue-600 text-white shadow-lg' 
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              <Zap className="w-4 h-4" />
              <span>Escape Regex</span>
            </button>
            <button
              onClick={() => setMode('unescape')}
              className={`px-6 py-2 rounded-md transition-all flex items-center space-x-2 ${
                mode === 'unescape' 
                  ? 'bg-purple-600 text-white shadow-lg' 
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              <RotateCcw className="w-4 h-4" />
              <span>Unescape Regex</span>
            </button>
          </div>
        </div>

        {/* Analytics Panel */}
        {showAnalytics && (
          <div className="mb-6 bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2" />
              Processing Analytics
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-blue-400">{stats.charactersProcessed.toLocaleString()}</div>
                <div className="text-gray-300 text-sm">Characters</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-purple-400">{stats.entitiesFound}</div>
                <div className="text-gray-300 text-sm">Escapes</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-red-400">{stats.processingTime.toFixed(2)}ms</div>
                <div className="text-gray-300 text-sm">Process Time</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-green-400">{stats.operationsCount}</div>
                <div className="text-gray-300 text-sm">Operations</div>
              </div>
            </div>
            
            {/* Regex Info */}
            <div className="mt-4 p-3 bg-gray-700 rounded-lg">
              <h4 className="text-white font-medium mb-2">Supported Regex Escape Sequences</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                <div className="text-gray-300">
                  <strong>Special:</strong> {"\\\\ \\^ \\$ \\. \\| \\? \\* \\+"}
                </div>
                <div className="text-gray-300">
                  <strong>Groups:</strong> {`\\( \\) \\[ \\] \\{ \\}`}
                </div>
                <div className="text-gray-300">
                  <strong>Control:</strong> {`\\n \\r \\t \\v \\f \\b \\0`}
                </div>
                <div className="text-gray-300">
                  <strong>Unicode:</strong> {`\\uXXXX \\u{XXXXX} \\xXX \\XXX`}
                </div>
                <div className="text-gray-300">
                  <strong>Shortcuts:</strong> {`\\s \\d \\w \\S \\D \\W`}
                </div>
                <div className="text-gray-300">
                  <strong>Safe:</strong> Pattern validation included
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-6 min-h-[600px]">
          {/* Input Section */}
          <div className="bg-gray-800 rounded-lg p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Input</h2>
              <div className="flex space-x-2">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".txt,.regex,.re,.pattern"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <div className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors">
                    <Upload className="w-4 h-4" />
                  </div>
                </label>
                <button
                  onClick={clearAll}
                  className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder={mode === 'escape' ? 'Enter regex pattern or text to escape...' : 'Enter escaped regex to unescape...'}
              className="flex-1 min-h-[500px] bg-gray-900 text-white p-4 rounded-lg border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none resize-none font-mono text-sm leading-relaxed"
            />
            
            <div className="mt-2 flex justify-between text-gray-400 text-sm">
              <span>Characters: {inputText.length.toLocaleString()}</span>
              <span>Lines: {inputText.split('\n').length}</span>
            </div>
          </div>

          {/* Output Section */}
          <div className="bg-gray-800 rounded-lg p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Output</h2>
              <div className="flex space-x-2">
                <button
                  onClick={testRegex}
                  disabled={!outputText}
                  className="p-2 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Search className="w-4 h-4" />
                </button>
                <button
                  onClick={copyToClipboard}
                  disabled={!outputText}
                  className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={downloadFile}
                  disabled={!outputText}
                  className="p-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <textarea
              value={outputText}
              readOnly
              placeholder="Processed regex output will appear here..."
              className="flex-1 min-h-[500px] bg-gray-900 text-white p-4 rounded-lg border border-gray-600 resize-none font-mono text-sm leading-relaxed"
            />
            
            <div className="mt-2 flex justify-between text-gray-400 text-sm">
              <span>Characters: {outputText.length.toLocaleString()}</span>
              <span>Lines: {outputText.split('\n').length}</span>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-6 bg-gray-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={loadSampleRegex}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
            >
              Load Sample Patterns
            </button>
            <button
              onClick={loadSampleEscaped}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
            >
              Load Sample Escaped
            </button>
            <button
              onClick={() => setMode(mode === 'escape' ? 'unescape' : 'escape')}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm"
            >
              Switch Mode
            </button>
            <button
              onClick={() => setInputText(inputText.replace(/\s+/g, ' ').trim())}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors text-sm"
            >
              Normalize Whitespace
            </button>
            <button
              onClick={testRegex}
              disabled={!outputText}
              className="px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-sm"
            >
              Validate Pattern
            </button>
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <div className="fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 z-50">
            <CheckCircle2 className="w-4 h-4" />
            <span>{notification}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default RegexEscapeUnescapeTool;